import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('pointerdown', this.#onDocumentClick);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  /**
   * Close open submenu when clicking/tapping outside the header menu.
   * pointerdown is more reliable than click on iOS for non-interactive nodes.
   * @param {PointerEvent | MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;
    if (this.overflowMenu?.contains(event.target)) return;
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Toggle the selected menu item on click
   * @param {PointerEvent | FocusEvent | MouseEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);

    if (!item) return;

    const isMoreSlot = Boolean(event.target.closest('[slot="more"]'));
    const hasPopup = item.getAttribute('aria-haspopup') === 'true' || isMoreSlot;

    // Leaf links navigate normally — do not open a submenu
    if (!hasPopup) return;

    event.preventDefault();
    event.stopPropagation();

    // Clicking the open parent again closes it
    if (item === this.#state.activeItem) {
      this.#deactivate(item);
      return;
    }

    // Clicking More while any overflow panel is open closes the panel
    if (isMoreSlot && this.dataset.overflowExpanded === 'true') {
      this.#deactivate(this.#state.activeItem);
      return;
    }

    const isOverflowItem = Boolean(event.target.closest('[slot="overflow"]'));
    const isDefaultSlot = !isOverflowItem && !isMoreSlot;

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) delete previousSubmenu.dataset.active;
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const submenuExists = Boolean(submenu);

    if (!submenuExists && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      submenu.dataset.active = '';
      this.#observeSubmenuHeight(submenu, submenuExists, isDefaultSlot);
    }

    this.#scheduleHeightSync(submenu, submenuExists, isDefaultSlot, item);

    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);

    document.removeEventListener('pointerdown', this.#onDocumentClick);
    // Defer so the opening click doesn’t immediately close the menu
    requestAnimationFrame(() => {
      document.addEventListener('pointerdown', this.#onDocumentClick);
    });
  };

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    document.removeEventListener('pointerdown', this.#onDocumentClick);

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item) ?? (item.closest('[slot="more"]') ? this.overflowMenu : null);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Collapse any expanded overflow parents when closing More
    this.querySelectorAll('[slot="overflow"] [aria-expanded="true"]').forEach((el) => {
      if (el instanceof HTMLElement) el.ariaExpanded = 'false';
    });

    if (submenu) {
      delete submenu.dataset.active;
    }
    if (this.overflowMenu) {
      delete this.overflowMenu.dataset.active;
    }
  };

  /**
   * @param {HTMLElement} submenu
   * @param {boolean} submenuExists
   * @param {boolean} isDefaultSlot
   */
  #observeSubmenuHeight(submenu, submenuExists, isDefaultSlot) {
    this.#cleanupMutationObserver();
    this.#submenuMutationObserver = new MutationObserver(() => {
      this.#scheduleHeightSync(submenu, submenuExists, isDefaultSlot);
      this.#cleanupMutationObserver();
    });
    this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });
    setTimeout(() => this.#cleanupMutationObserver(), 500);
  }

  /**
   * Measure now and again after layout settles.
   * @param {HTMLElement | null | undefined} submenu
   * @param {boolean} submenuExists
   * @param {boolean} isDefaultSlot
   * @param {HTMLElement | null} [activeItem]
   */
  #scheduleHeightSync(submenu, submenuExists, isDefaultSlot, activeItem = this.#state.activeItem) {
    this.#syncOverflowHeight(submenu, submenuExists, isDefaultSlot);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (activeItem && this.#state.activeItem !== activeItem) return;
        this.#syncOverflowHeight(submenu, submenuExists, isDefaultSlot);
      });
    });
  }

  /**
   * @param {HTMLElement | null | undefined} submenu
   * @param {boolean} submenuExists
   * @param {boolean} isDefaultSlot
   */
  #syncOverflowHeight = (submenu, submenuExists, isDefaultSlot) => {
    if (!this.headerComponent) return;

    let finalHeight = submenu?.offsetHeight || 0;

    if (!isDefaultSlot) {
      if (submenuExists) {
        // Prefer live panel height — avoid display:none flash while measuring
        finalHeight = Math.max(this.overflowMenu?.offsetHeight || 0, submenu?.offsetHeight || 0);
      } else {
        finalHeight = this.#getOverflowListLinksHeight();
      }
    }

    if (!submenu) {
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const moreItem = element.closest('[slot="more"]');
  if (moreItem) {
    // Keep "More" as the active trigger so the first overflow item
    // is not auto-expanded (avoids a flashy / janky open).
    const moreButton = moreItem.querySelector('[aria-haspopup="true"], .menu-list__link');
    return moreButton instanceof HTMLElement ? moreButton : null;
  }

  if (element.matches('[ref="menuitem"]')) {
    return /** @type {HTMLElement} */ (element);
  }

  const listItem = element.closest('.menu-list__list-item');
  if (listItem) {
    const menuItem = listItem.querySelector('[ref="menuitem"]');
    return menuItem instanceof HTMLElement ? menuItem : null;
  }

  return null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
